import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionConversation,
  Chat,
  Header,
  Loading,
  PageWrapper,
  Text,
} from 'components';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { HTTP_STATUS, TEXT_STRING, queryKeys, routes } from '../../constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createChannel,
  getChannelDetail,
  getClientChannelDetail,
  getProjectFemales,
} from 'api';
import { AxiosError } from 'axios';
import { useUserStore } from 'store';
import {
  ApproveRejectMode,
  EChannelType,
  EUserProvider,
  TCreateChannel,
} from 'types';
import ApproveReject from 'components/Conversation/ActionConversation/ApproveReject';

const readonlyConversation = [EChannelType.MALE_SYSTEM_MANAGEMENT];

function Conversation() {
  // Hook
  const queryClient = useQueryClient();
  const { user, clientId, isAuthenticated } = useUserStore();
  const { id: channel } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const createId = searchParams.get('create-id');
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [showAction, setShowAction] = useState<boolean>(true);

  // Query
  const {
    data,
    error,
    isLoading: loadingChannelDetail,
    refetch,
    isError,
  } = useQuery({
    queryKey: [queryKeys.CHANNEL_DETAIL, channel],
    queryFn: () => getChannelDetail(channel || ''),
    enabled: !!isAuthenticated,
    retry: 0,
  });
  const { data: clientData, isLoading } = useQuery({
    queryKey: [queryKeys.CLIENT_CHANNEL_DETAIL, channel],
    queryFn: () => getClientChannelDetail(channel || ''),
    enabled: !isAuthenticated,
    retry: 0,
  });
  const {
    data: females,
    isFetching,
    refetch: refetchFemales,
  } = useQuery({
    queryKey: [queryKeys.PROJECT_FEMALES, data?.projectId],
    queryFn: () => getProjectFemales(data?.projectId || 0),
    enabled: !!data?.projectId,
  });

  const channelData = useMemo(
    () => (isAuthenticated ? data : clientData),
    [isAuthenticated, data, clientData],
  );

  // Mutation
  const { mutateAsync } = useMutation({
    mutationFn: (body: TCreateChannel) => {
      return createChannel(body);
    },
  });

  const isCreateNew = useMemo(() => {
    return (
      !channelData &&
      (error as AxiosError)?.status === HTTP_STATUS.NOT_FOUND &&
      createId &&
      !isNaN(+createId)
    );
  }, [channelData, createId, error]);

  const channelParticipants = useMemo(
    () =>
      channelData?.participant?.filter(
        (member) => ![user?.uuid, clientId].includes(member.uuid),
      ),
    [channelData?.participant, user?.uuid, clientId],
  );

  const isReadOnly = useMemo(() => {
    const maleParticipants =
      channelData?.participant?.filter(
        (member) => member.provider === EUserProvider.MALE_APPLICATION,
      ) || [];

    return (
      (channelData?.type && readonlyConversation.includes(channelData?.type)) ||
      (channelData?.type === EChannelType.SALON &&
        maleParticipants.length > 0 &&
        !maleParticipants.every((member) => !!member.isSalonMember)) ||
      (channelData?.participant
        ?.filter(
          (member) => member.provider === EUserProvider.FEMALE_APPLICATION,
        )
        ?.every((member) => member.isReviewed) &&
        channelData?.type === EChannelType.PROJECT)
    );
  }, [channelData?.participant, channelData?.type]);

  const onCreateChannel = useCallback(async () => {
    if (channel && createId) {
      const res = await mutateAsync({ id: channel, femaleId: +createId });
      refetch();
      return res;
    }
  }, [channel, createId, mutateAsync, refetch]);

  const hasPendingAction = useMemo(() => {
    return !!(
      channelData?.firstPendingInvitedProjectOrderId ||
      channelData?.firstPendingAgreementProjectOrderId
    );
  }, [
    channelData?.firstPendingInvitedProjectOrderId,
    channelData?.firstPendingAgreementProjectOrderId,
  ]);

  const onFocusInput = useCallback(() => {
    if (hasPendingAction) return;
    if (channelData?.projectId) {
      setShowAction(false);
    }
  }, [channelData?.projectId, hasPendingAction]);

  const onBlurInput = useCallback(() => {
    if (channelData?.projectId || hasPendingAction) {
      setShowAction(true);
    }
  }, [channelData?.projectId, hasPendingAction]);

  const handleRefetch = useCallback(() => {
    refetch();
    refetchFemales();
    if (data?.projectId) {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.GAMES, data.projectId],
      });
    }
  }, [data?.projectId, queryClient, refetch, refetchFemales]);

  useEffect(() => {
    if (createId && channelData) {
      setSearchParams();
    }
    return () => {};
  }, [createId, channelData, searchParams, setSearchParams]);

  return (
    <PageWrapper
      key={channel}
      className="flex flex-col justify-between items-center w-full h-full text-sm"
    >
      {loadingChannelDetail || isLoading ? (
        <div className="h-full flex items-center">
          <Loading />
        </div>
      ) : (
        <>
          {(!!channelData || isCreateNew) && !isError ? (
            <>
              <div className="flex-1 overflow-hidden w-full h-full translate-x-0 translate-y-0">
                <Chat
                  channel={channel || ''}
                  channelData={channelData}
                  users={channelParticipants}
                  onCreateChannel={isCreateNew ? onCreateChannel : undefined}
                  onBlur={onBlurInput}
                  onFocus={onFocusInput}
                  refetch={handleRefetch}
                  refetchChannelDetail={refetch}
                  action={(() => {
                    if (!showAction) return;
                    if (!channelData?.projectId && !hasPendingAction) {
                      return;
                    }
                    if (data?.firstPendingAgreementProjectOrderId) {
                      return (
                        <ApproveReject
                          firstPendingInvitedProjectOrderId={
                            data.firstPendingAgreementProjectOrderId
                          }
                          refetch={handleRefetch}
                          loading={loadingChannelDetail}
                          mode={ApproveRejectMode.STEP2_AGREEMENT}
                        />
                      );
                    }

                    if (data?.firstPendingInvitedProjectOrderId) {
                      return (
                        <ApproveReject
                          firstPendingInvitedProjectOrderId={
                            data.firstPendingInvitedProjectOrderId
                          }
                          refetch={handleRefetch}
                          loading={loadingChannelDetail}
                          mode={ApproveRejectMode.STEP1_PROJECT_INFO}
                        />
                      );
                    }

                    if (channelData?.projectId) {
                      return (
                        <ActionConversation
                          channelDetail={channelData}
                          refetch={handleRefetch}
                          females={females}
                          isLoading={isFetching}
                        />
                      );
                    }
                  })()}
                  readonly={isReadOnly}
                />
              </div>
            </>
          ) : (
            <>
              {isAuthenticated ? (
                <div className="h-full w-full flex flex-col justify-between items-center">
                  <Header
                    title={<></>}
                    onBack={() => navigate(routes.MESSAGE)}
                  />
                  <Text center bold fontSize={16}>
                    {TEXT_STRING.COMMON.MESSAGE_REMOVED_CHANNEL}
                  </Text>
                  <div></div>
                </div>
              ) : (
                <Navigate
                  to={routes.LOGIN}
                  state={{ from: location }}
                  replace
                />
              )}
            </>
          )}
        </>
      )}
    </PageWrapper>
  );
}

export default Conversation;
